import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trash2, Edit, Eye } from 'lucide-react';
import { useDeleteArticleAdmin, useUpdateArticleAdmin } from '@/hooks/useArticlesAdmin';
import type { Database } from '@/types/database.types';
import { cn } from '@/lib/utils';

type Article = Database['public']['Tables']['articles']['Row'];

interface ArticlesTableProps {
  articles: Article[];
  isLoading?: boolean;
}

/**
 * Obtenir la couleur du badge selon le statut
 */
function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'published':
      return 'default';
    case 'draft':
      return 'secondary';
    case 'archived':
      return 'outline';
    default:
      return 'secondary';
  }
}

/**
 * Obtenir le label du statut en français
 */
function getStatusLabel(status: string) {
  switch (status) {
    case 'published':
      return 'Publié';
    case 'draft':
      return 'Brouillon';
    case 'archived':
      return 'Archivé';
    default:
      return status;
  }
}

/**
 * Table de gestion des articles (admin)
 */
export function ArticlesTable({ articles, isLoading }: ArticlesTableProps) {
  const { locale } = useParams();
  const navigate = useNavigate();
  const currentLocale = locale || 'fr';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
  const deleteArticle = useDeleteArticleAdmin();
  const updateArticle = useUpdateArticleAdmin();
  // Stocker l'ID de l'article en cours de mise à jour statut
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  const handleStatusChange = async (articleId: string, newStatus: string) => {
    setUpdatingStatusId(articleId);
    try {
      const updates: Record<string, unknown> = { status: newStatus };
      // Si on publie et qu'il n'y a pas de date de publication, la mettre à maintenant
      if (newStatus === 'published') {
        const article = articles.find((a) => a.id === articleId);
        if (article && !article.published_at) {
          updates.published_at = new Date().toISOString();
        }
      }
      await updateArticle.mutateAsync({ id: articleId, updates });
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDeleteClick = (article: Article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;

    try {
      await deleteArticle.mutateAsync(articleToDelete.id);
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-sm text-gray-500">Chargement des articles...</p>
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-sm text-gray-500">Aucun article trouvé</p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="text-right">Vues</TableHead>
              <TableHead>Date publication</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                {/* Titre */}
                <TableCell className="max-w-md">
                  <div className="space-y-1">
                    <p
                      className="font-medium text-sm truncate"
                      title={article.title}
                    >
                      {article.title}
                    </p>
                    {article.is_premium && (
                      <Badge variant="outline" className="text-xs">
                        Premium
                      </Badge>
                    )}
                  </div>
                </TableCell>

                {/* Statut (Select inline) */}
                <TableCell>
                  <Select
                    value={article.status}
                    onValueChange={(value) => handleStatusChange(article.id, value)}
                    disabled={updatingStatusId === article.id}
                  >
                    <SelectTrigger size="sm" className={cn(
                      'w-[130px]',
                      updatingStatusId === article.id && 'opacity-50'
                    )}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Publié</SelectItem>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="archived">Archivé</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>

                {/* Catégorie */}
                <TableCell>
                  <span className="text-sm text-gray-600">
                    {article.category || '—'}
                  </span>
                </TableCell>

                {/* Vues */}
                <TableCell className="text-right">
                  <span className="text-sm font-medium">
                    {(article.view_count || 0).toLocaleString('fr-FR')}
                  </span>
                </TableCell>

                {/* Date publication */}
                <TableCell>
                  {article.published_at ? (
                    <span className="text-sm text-gray-600">
                      {format(new Date(article.published_at), 'dd MMM yyyy', {
                        locale: fr,
                      })}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Non publié</span>
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {/* Voir l'article */}
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      title="Voir l'article"
                    >
                      <a
                        href={`/${currentLocale}/blog/${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                    </Button>

                    {/* Éditer */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/${currentLocale}/admin/articles/${article.id}/edit`)}
                      title="Éditer"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>

                    {/* Supprimer */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(article)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer l'article "{articleToDelete?.title}" ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteArticle.isPending}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteArticle.isPending}
            >
              {deleteArticle.isPending ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
